import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Glisser vers le bas pour actualiser — acquis de #34, demandé en plus du lien
 * « Actualiser » (plan 2026-09-23, D2).
 *
 * Repris de `pages/housekeeping/helpers/usePullToRefresh.ts` SANS import (frontière
 * #23), avec trois écarts voulus :
 * 1. les écouteurs vivent sur le CONTENEUR de la liste, pas sur `document` : le geste
 *    ne part jamais d'une feuille, du panneau ou d'un e-mail plein écran ;
 * 2. un glissement à dominante horizontale est ignoré : le bord gauche est le retour
 *    système (#34), on ne le lui dispute pas ;
 * 3. tous les écouteurs sont passifs : on ne bloque jamais le défilement natif.
 *
 * Le lien « Actualiser » reste le chemin accessible : un geste de tirage n'est pas
 * praticable avec VoiceOver.
 */
interface Options {
    seuil?: number;
    max?: number;
    actif?: boolean;
}

export function useTirerPourActualiser(
    refCorps: React.RefObject<HTMLElement>,
    onActualiser: () => void,
    { seuil = 70, max = 100, actif = true }: Options = {},
) {
    const [distance, setDistanceEtat] = useState(0);
    const distanceRef = useRef(0);
    const depart = useRef<{ x: number; y: number } | null>(null);
    const rappel = useRef(onActualiser);
    rappel.current = onActualiser;

    const frameEnAttente = useRef<number | null>(null);

    /*
     * Un rendu par frame au plus (audit 2026-09-23). `touchmove` tombe à 60-120 Hz :
     * un `setState` par événement re-rendait la liste plusieurs fois par frame
     * pendant tout le geste. La distance RÉELLE vit dans `distanceRef`, lue sans
     * délai par la logique (seuil au relâcher) ; seul son AFFICHAGE attend la frame.
     */
    const annulerFrame = useCallback(() => {
        if (frameEnAttente.current === null) return;
        cancelAnimationFrame(frameEnAttente.current);
        frameEnAttente.current = null;
    }, []);

    const setDistanceParFrame = useCallback((d: number) => {
        distanceRef.current = d;
        if (frameEnAttente.current !== null) return;
        frameEnAttente.current = requestAnimationFrame(() => {
            frameEnAttente.current = null;
            setDistanceEtat(distanceRef.current);
        });
    }, []);

    /** Remise à zéro : immédiate, et la frame en attente n'a plus rien à rendre. */
    const setDistance = useCallback(
        (d: number) => {
            annulerFrame();
            distanceRef.current = d;
            setDistanceEtat(d);
        },
        [annulerFrame],
    );

    useEffect(() => annulerFrame, [annulerFrame]);

    useEffect(() => {
        const corps = refCorps.current;
        if (!corps || !actif) return;

        /** Plusieurs doigts = un pincement (zoom autorisé) : jamais un tirage (relecture M2). */
        const annulerSiMultiDoigts = (e: TouchEvent) => {
            if (e.touches.length <= 1) return false;
            depart.current = null;
            if (distanceRef.current > 0) setDistance(0);
            return true;
        };

        const debut = (e: TouchEvent) => {
            if (annulerSiMultiDoigts(e)) return;
            // Seulement tout en haut : ailleurs, glisser vers le bas est un défilement.
            depart.current = corps.scrollTop <= 0 ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : null;
        };

        const mouvement = (e: TouchEvent) => {
            if (annulerSiMultiDoigts(e)) return;
            if (!depart.current) return;
            const dx = e.touches[0].clientX - depart.current.x;
            const dy = e.touches[0].clientY - depart.current.y;
            if (Math.abs(dx) > Math.abs(dy)) {
                // Geste horizontal : c'est au système. On lâche pour tout le geste.
                depart.current = null;
                if (distanceRef.current > 0) setDistance(0);
                return;
            }
            // Amorti de moitié, plafonné : le tirage résiste, comme sur iOS.
            setDistanceParFrame(dy > 0 ? Math.min(max, dy * 0.5) : 0);
        };

        const fin = () => {
            if (!depart.current && distanceRef.current === 0) return;
            depart.current = null;
            const atteint = distanceRef.current >= seuil;
            setDistance(0);
            if (atteint) rappel.current();
        };

        corps.addEventListener('touchstart', debut, { passive: true });
        corps.addEventListener('touchmove', mouvement, { passive: true });
        corps.addEventListener('touchend', fin);
        corps.addEventListener('touchcancel', fin);
        return () => {
            corps.removeEventListener('touchstart', debut);
            corps.removeEventListener('touchmove', mouvement);
            corps.removeEventListener('touchend', fin);
            corps.removeEventListener('touchcancel', fin);
        };
    }, [refCorps, actif, seuil, max, setDistance, setDistanceParFrame]);

    return { distance, pret: distance >= seuil };
}
